'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  X,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  BarChart3,
  Binoculars,
  Star,
  Minus,
  Info,
  BadgeCheck,
  Users,
  Layers,
  Copy,
  Check,
  Globe,
  Twitter,
  Share2
} from 'lucide-react';
import TokenImage from '@/components/ui/TokenImage';
import Tooltip from '@/components/ui/Tooltip';
import { ChainBadge } from '@/components/ui/ChainBadge';
import { useMarketAnalysis, getTokenInsight, getTimeAgo, isDataStale } from '@/hooks/useMarketAnalysis';
import { useCollectionAnalysis, getCollectionInsight, getTimeAgo as getCollectionTimeAgo, isDataStale as isCollectionDataStale } from '@/hooks/useCollectionAnalysis';
import { useFirestoreCrypto } from '@/hooks/useFirestoreCrypto';
import { useFirestoreCollectionStat, useFirestoreCollectionStats } from '@/hooks/useFirestoreCollectionStats';
import GlobalMarketStats from '@/components/market/GlobalMarketStats';
import { SentimentVoteButtons } from '@/components/voting';
import TagAssetShareModal from '@/components/market/TagAssetShareModal';
import TokenShareModal from '@/components/market/TokenShareModal';
import NFTShareModal from '@/components/market/NFTShareModal';
import { getCollectionBlockchain, getCollectionDisplayName } from '@/utils/blockchain';
import { entityUrl } from '@/lib/entity-url';

/**
 * Sentiment Badge Component for market overview
 */
function SentimentIndicator({ sentiment }: { sentiment: number }) {
  if (sentiment > 0) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-800/50 rounded-lg">
        <TrendingUp size={14} className="text-emerald-600 dark:text-signal-green-text" />
        <span className="font-mono text-xs font-bold text-emerald-600 dark:text-signal-green-text">BULLISH</span>
      </div>
    );
  } else if (sentiment < 0) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-rose-100 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-800/50 rounded-lg">
        <TrendingDown size={14} className="text-rose-600 dark:text-alert-red-text" />
        <span className="font-mono text-xs font-bold text-rose-600 dark:text-alert-red-text">BEARISH</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-neutral-100 dark:bg-surface2 border border-neutral-300 dark:border-divider rounded-lg">
      <Minus size={14} className="text-neutral-500 dark:text-neutral-400" />
      <span className="font-mono text-xs font-bold text-neutral-500 dark:text-neutral-400">NEUTRAL</span>
    </div>
  );
}

interface AssetDetailPanelProps {
  asset: {
    id: string;
    type: 'token' | 'nft';
    name: string;
    symbol?: string;
    slug?: string;
    image?: string;
    price: number;
    priceFormatted: string;
    change24h: number;
    hasChangeData: boolean;
    volume24h: number;
    volumeFormatted: string;
    marketCap?: number;
    marketCapFormatted?: string;
    sales?: number;
    currency: string;
    featured?: boolean;
    tradingUrl?: string;
    address?: string;
    hasGlossaryPage?: boolean;
    /** The glossary tag's slug — the direct dossier door (`/tags/<slug>/`). */
    glossarySlug?: string;
    // OpenSea metadata
    isVerified?: boolean;
    description?: string;
    // Additional OpenSea stats (NFT collections)
    numOwners?: number;
    totalSupply?: number;
    averagePrice?: number;
    sevenDayVolume?: number;
    sevenDayChange?: number;
    thirtyDayVolume?: number;
    thirtyDayChange?: number;
    // Social links from OpenSea
    externalUrl?: string;
    twitterUsername?: string;
    discordUrl?: string;
    instagramUsername?: string;
    // Contract info
    contracts?: Array<{ address: string; chain: string }>;
    // Created date
    createdDate?: string;
  } | null;
  onClose: () => void;
  className?: string;
}

/**
 * Format text to highlight tokens, numbers, and metrics
 */
function formatScannableText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const items: Array<{ index: number; length: number; element: React.ReactNode }> = [];

  // Find token symbols
  const tokenRegex = /\b([A-Z]{2,})\b/g;
  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    items.push({
      index: match.index,
      length: match[0].length,
      element: <strong key={`token-${match.index}`} className="font-semibold text-emerald-600 dark:text-signal-green-text">{match[0]}</strong>
    });
  }

  // Find dollar amounts
  const moneyRegex = /\$[\d,]+\.?\d*[KMB]?/gi;
  const moneyMatches = text.matchAll(moneyRegex);
  for (const numMatch of moneyMatches) {
    items.push({
      index: numMatch.index!,
      length: numMatch[0].length,
      element: <span key={`money-${numMatch.index}`} className="font-semibold text-neutral-900 dark:text-parchment">{numMatch[0]}</span>
    });
  }

  // Find percentages
  const percentRegex = /[+-]?\d+\.?\d*%/gi;
  const percentMatches = text.matchAll(percentRegex);
  for (const numMatch of percentMatches) {
    const numText = numMatch[0];
    const isNegative = numText.startsWith('-');
    const isPositive = numText.startsWith('+');
    items.push({
      index: numMatch.index!,
      length: numText.length,
      element: (
        <span
          key={`pct-${numMatch.index}`}
          className={`font-semibold ${isNegative ? 'text-rose-600 dark:text-alert-red-text' : isPositive ? 'text-emerald-600 dark:text-signal-green-text' : 'text-neutral-900 dark:text-parchment'}`}
        >
          {numText}
        </span>
      )
    });
  }

  // Sort and remove overlaps
  items.sort((a, b) => a.index - b.index);
  const filteredItems: typeof items = [];
  let lastEnd = 0;
  for (const item of items) {
    if (item.index >= lastEnd) {
      filteredItems.push(item);
      lastEnd = item.index + item.length;
    }
  }

  filteredItems.forEach((item) => {
    if (item.index > lastIndex) {
      parts.push(text.slice(lastIndex, item.index));
    }
    parts.push(item.element);
    lastIndex = item.index + item.length;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Asset Detail Panel - Right side panel showing selected asset details
 */
export default function AssetDetailPanel({ asset, onClose, className = '' }: AssetDetailPanelProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTokenOverviewShare, setShowTokenOverviewShare] = useState(false);
  const [showNFTOverviewShare, setShowNFTOverviewShare] = useState(false);

  // Copy contract address to clipboard
  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (err) {
      console.error('Failed to copy address');
    }
  };
  
  // Get market analysis data - includes both individual insights and pulse
  const { insights, pulse: tokenPulse, loading: insightsLoading } = useMarketAnalysis();
  const { insights: collectionInsights, pulse: nftPulse, loading: collectionLoading } = useCollectionAnalysis();
  
  // Get additional data for share functionality
  const { data: cryptoData } = useFirestoreCrypto();
  const { stats: collectionStats } = useFirestoreCollectionStat(asset?.type === 'nft' ? asset?.slug || '' : '');
  
  // Get all collection stats for NFT share modal (to match market page behavior)
  const { allStats } = useFirestoreCollectionStats({ enableRealTimeUpdates: true });

  // Mark as mounted (client-side only) to avoid hydration mismatch
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get token insight if available (only after mount to avoid hydration issues)
  const tokenInsight = hasMounted && asset?.type === 'token' && asset.symbol
    ? getTokenInsight(insights, asset.symbol)
    : null;
  
  const hasValidInsight = hasMounted && tokenInsight && !isDataStale(tokenInsight.timestamp, 12);
  
  // Get NFT collection insight if available
  const collectionInsight = hasMounted && asset?.type === 'nft' && asset.slug
    ? getCollectionInsight(collectionInsights, asset.slug)
    : null;
  
  const hasValidCollectionInsight = hasMounted && collectionInsight && !isCollectionDataStale(collectionInsight.timestamp, 12);

  // Market pulse for overview (only check after mount)
  const hasTokenPulse = hasMounted && tokenPulse && !isDataStale(tokenPulse.timestamp, 12);
  const hasNftPulse = hasMounted && nftPulse && !isDataStale(nftPulse.timestamp, 12);

  // Calculate combined sentiment for market overview
  const tokenSentiment = hasTokenPulse 
    ? (tokenPulse.metrics.gainersCount > tokenPulse.metrics.losersCount ? 1 : tokenPulse.metrics.losersCount > tokenPulse.metrics.gainersCount ? -1 : 0)
    : 0;
  const nftSentiment = hasNftPulse
    ? (nftPulse.metrics.activeCollections > nftPulse.metrics.totalCollections / 2 ? 1 : 0)
    : 0;
  const combinedSentiment = tokenSentiment + nftSentiment > 0 ? 1 : tokenSentiment + nftSentiment < 0 ? -1 : 0;

  // Compute top gainers/losers with images for share modal
  const abstractTokens = cryptoData?.abstract_tokens?.filter(
    t => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM', 'PENGU'].includes(t.symbol.toUpperCase())
  ) || [];
  
  const top5Gainers = [...abstractTokens]
    .filter(t => (t.priceChange24h || 0) > 0)
    .sort((a, b) => (b.priceChange24h || 0) - (a.priceChange24h || 0))
    .slice(0, 5)
    .map(t => ({ 
      symbol: t.symbol, 
      change: t.priceChange24h || 0,
      imageUrl: t.customImage || t.imageUrl || null,
    }));
  
  const top5Losers = [...abstractTokens]
    .filter(t => (t.priceChange24h || 0) < 0)
    .sort((a, b) => (a.priceChange24h || 0) - (b.priceChange24h || 0))
    .slice(0, 5)
    .map(t => ({ 
      symbol: t.symbol, 
      change: t.priceChange24h || 0,
      imageUrl: t.customImage || t.imageUrl || null,
    }));

  // Compute NFT highlights for share modal (same logic as market page)
  const abstractChainCollections = Object.values(allStats || {}).filter(stat => {
    const blockchain = getCollectionBlockchain(stat.collection);
    return blockchain === 'abstract';
  });

  const nftHighlights = abstractChainCollections
    .filter(stat => (stat.oneDayVolume || 0) > 0)
    .sort((a, b) => (b.oneDayVolume || 0) - (a.oneDayVolume || 0))
    .slice(0, 3)
    .map(stat => ({
      name: stat.collection,
      displayName: getCollectionDisplayName(stat.collection),
      // floorPrice widened to number|null (WS-K); NFTHighlight expects number and
      // these highlight cards treat absent-floor as 0 downstream — coalesce here.
      floorPrice: stat.floorPrice ?? 0,
      floorSymbol: stat.floorPriceSymbol || 'ETH',
      oneDayChange: stat.oneDayChange,
      volume24h: stat.oneDayVolume || 0,
      imageUrl: stat.imageUrl || null,
    }));

  const totalNFTVolume24h = abstractChainCollections.reduce(
    (sum, stat) => sum + (stat.oneDayVolume || 0), 
    0
  );

  const abstractNftCount = abstractChainCollections.length;

  useEffect(() => {
    if (asset) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [asset]);

  // Check if this is being rendered in mobile drawer (no rounded corners, no border)
  const isMobileDrawer = className.includes('rounded-none') || className.includes('border-0');

  if (!asset) {
    // Show Market Overview instead of empty state - Compact version
    return (
      <div className={`bg-white dark:bg-surface border border-neutral-200 dark:border-divider rounded-xl overflow-hidden ${className}`}>
        {/* Header - Compact */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-200 dark:border-divider bg-neutral-50 dark:bg-surface">
          <span className="font-mono text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Market Overview
          </span>
          <SentimentIndicator sentiment={combinedSentiment} />
        </div>

        {/* Scout's Take - AI Analysis - Compact */}
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
          <div className="flex items-center gap-2 mb-2">
            <Binoculars size={12} className="text-scout-gold" />
            <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-parchment uppercase tracking-wider">RECON Intel</span>
            <Tooltip content="Market analysis covers Abstract ecosystem tokens & NFTs" position="bottom">
              <Info size={10} className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-parchment/85 cursor-help transition-colors" />
            </Tooltip>
          </div>
          
          {insightsLoading || collectionLoading ? (
            <div className="animate-pulse space-y-1.5">
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-full" />
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-3/4" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Token Market Summary */}
              {hasTokenPulse && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-bold text-emerald-600 dark:text-signal-green-text uppercase tracking-wider">Tokens</span>
                    <div className="flex-1 h-px bg-neutral-200 dark:bg-surface2" />
                    <button
                      onClick={() => setShowTokenOverviewShare(true)}
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700/50 text-emerald-600 dark:text-signal-green-text hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-colors"
                      title="Share token market overview"
                    >
                      <Share2 size={12} />
                      <span className="text-[10px] font-mono font-bold">Share</span>
                    </button>
                  </div>
                  <p className="font-mono text-xs text-neutral-600 dark:text-parchment/85 leading-relaxed">
                    {formatScannableText(tokenPulse.summary)}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="text-emerald-600 dark:text-signal-green-text">{tokenPulse.metrics.gainersCount} ↑</span>
                    <span className="text-rose-600 dark:text-alert-red-text">{tokenPulse.metrics.losersCount} ↓</span>
                    <span className="text-neutral-500">• {getTimeAgo(tokenPulse.timestamp)}</span>
                  </div>
                </div>
              )}

              {/* NFT Market Summary */}
              {hasNftPulse && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-bold text-amber-600 dark:text-scout-gold uppercase tracking-wider">NFTs</span>
                    <div className="flex-1 h-px bg-neutral-200 dark:bg-surface2" />
                    <button
                      onClick={() => setShowNFTOverviewShare(true)}
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 dark:bg-scout-gold-dim border border-amber-300 dark:border-scout-gold/30 text-amber-600 dark:text-scout-gold hover:bg-amber-200 dark:hover:bg-scout-gold/20 transition-colors"
                      title="Share NFT market overview"
                    >
                      <Share2 size={12} />
                      <span className="text-[10px] font-mono font-bold">Share</span>
                    </button>
                  </div>
                  <p className="font-mono text-xs text-neutral-600 dark:text-parchment/85 leading-relaxed">
                    {formatScannableText(nftPulse.summary)}
                  </p>
                  <div className="flex items-center flex-wrap gap-2 text-[10px] font-mono">
                    <span className="text-amber-600 dark:text-scout-gold">{nftPulse.metrics.totalCollections} tracked</span>
                    <span className="text-neutral-500">• {getCollectionTimeAgo(nftPulse.timestamp)}</span>
                  </div>
                </div>
              )}

              {!hasTokenPulse && !hasNftPulse && (
                <p className="font-mono text-xs text-neutral-500 italic">
                  Market analysis loading...
                </p>
              )}
            </div>
          )}
        </div>

        {/* Global Market Context Section - Compact */}
        {tokenPulse?.globalContext?.globalMarket && (
          <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
            <GlobalMarketStats 
              data={tokenPulse.globalContext.globalMarket}
              fearGreed={tokenPulse.globalContext.fearGreed ?? undefined}
              variant="compact"
            />
          </div>
        )}

        {/* Prompt to select - Compact */}
        <div className="px-4 py-2.5 bg-neutral-100 dark:bg-ink/60">
          <p className="text-neutral-500 text-[10px] font-mono text-center">
            Select an asset from the list for detailed analysis
          </p>
        </div>

        {/* Token Overview Share Modal */}
        {tokenPulse && (
          <TokenShareModal
            isOpen={showTokenOverviewShare}
            onClose={() => setShowTokenOverviewShare(false)}
            pulse={tokenPulse}
            abstractFearGreed={tokenPulse.abstractFearGreed}
            topGainers={top5Gainers}
            topLosers={top5Losers}
          />
        )}

        {/* NFT Overview Share Modal */}
        {nftPulse && (
          <NFTShareModal
            isOpen={showNFTOverviewShare}
            onClose={() => setShowNFTOverviewShare(false)}
            collectionPulse={nftPulse}
            nftHighlights={nftHighlights}
            nftCount={abstractNftCount}
            totalVolume24h={totalNFTVolume24h}
            abstractFearGreed={tokenPulse?.abstractFearGreed}
          />
        )}
      </div>
    );
  }

  // Door straight to the entity dossier when we know its slug — ONE soft nav that
  // fires /tags/[slug]/loading.tsx immediately. The legacy /coins | /collections
  // paths (a server redirect hop with no loading boundary — the "long load" on
  // PENGU, 2026-06-12) remain only as a fallback while the slug resolves.
  const profileUrl = asset.glossarySlug
    ? entityUrl(asset.glossarySlug, { hash: 'market' })
    : asset.type === 'token'
      ? `/coins/${asset.symbol?.toLowerCase()}`
      : `/collections/${asset.slug}`;

  const tradeUrl = asset.type === 'token'
    ? asset.tradingUrl || `https://dexscreener.com/abstract/${asset.address}`
    : `https://opensea.io/collection/${asset.slug}`;

  return (
    <div
      className={`bg-white dark:bg-surface overflow-hidden transition-all duration-300 ${
        isMobileDrawer ? '' : 'border border-neutral-200 dark:border-divider rounded-xl'
      } ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      } ${className}`}
    >
      {/* Header with close button - Compact */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-200 dark:border-divider bg-neutral-50 dark:bg-surface">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Asset Details
          </span>
          <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
            asset.type === 'token'
              ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-signal-green-text border border-emerald-300 dark:border-emerald-800/50'
              : 'bg-amber-100 dark:bg-scout-gold-dim text-amber-700 dark:text-scout-gold border border-amber-300 dark:border-scout-gold/30'
          }`}>
            {asset.type === 'token' ? 'TKN' : 'NFT'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Share Button */}
          <button
            onClick={() => setShowShareModal(true)}
            className={`p-1.5 rounded-lg transition-colors ${
              asset.type === 'token'
                ? 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-neutral-400 dark:text-neutral-500 hover:text-emerald-600 dark:hover:text-signal-green-text'
                : 'hover:bg-amber-100 dark:hover:bg-scout-gold-dim text-neutral-400 dark:text-neutral-500 hover:text-amber-600 dark:hover:text-scout-gold'
            }`}
            title="Share asset"
          >
            <Share2 size={16} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-surface2 text-neutral-400 dark:text-neutral-500 hover:text-neutral-900 dark:hover:text-parchment transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Asset Header + Price Combined - More Compact */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
        <div className="flex items-center gap-3">
          {/* Asset Image - Smaller */}
          <div className="relative flex-shrink-0">
            {asset.type === 'token' ? (
              <TokenImage
                symbol={asset.symbol || ''}
                address={asset.address || ''}
                name={asset.name}
                imageUrl={asset.image}
                size={48}
                className="rounded-lg"
              />
            ) : asset.image ? (
              <img
                src={asset.image}
                alt={asset.name}
                className="w-12 h-12 object-cover rounded-lg"
              />
            ) : (
              <div className="w-12 h-12 flex items-center justify-center text-neutral-900 dark:text-parchment text-xl font-bold bg-neutral-200 dark:bg-surface2 rounded-lg">
                {asset.name?.charAt(0) || '?'}
              </div>
            )}
            {asset.featured && (
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                <Star size={10} className="text-neutral-900 fill-current" />
              </div>
            )}
          </div>

          {/* Asset Info + Price Inline */}
          <div className="flex-1 min-w-0">
            <h2 className="font-mono text-lg font-bold text-neutral-900 dark:text-parchment truncate">
              {asset.type === 'token' ? asset.symbol : asset.name}
            </h2>
            <p className="font-mono text-xs text-neutral-500 truncate">
              {asset.type === 'token' ? asset.name : asset.slug}
            </p>
          </div>
        </div>
      </div>

      {/* Price Section - Compact */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider bg-neutral-50 dark:bg-ink/60">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
              {asset.type === 'token' ? 'PRICE' : 'FLOOR PRICE'}
            </span>
            <span className="font-mono text-2xl font-bold text-neutral-900 dark:text-parchment">
              {asset.priceFormatted}
            </span>
          </div>
          <div className="text-right">
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
              24H CHANGE
            </span>
            {asset.hasChangeData ? (
              <div className={`flex items-center justify-end gap-1 font-mono text-lg font-bold ${
                asset.change24h >= 0 ? 'text-emerald-600 dark:text-signal-green-text' : 'text-rose-600 dark:text-alert-red-text'
              }`}>
                {asset.change24h >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {asset.change24h >= 0 ? '+' : ''}{asset.change24h.toFixed(2)}%
              </div>
            ) : (
              <span className="font-mono text-lg text-neutral-400 dark:text-neutral-600">—</span>
            )}
          </div>
        </div>
      </div>

      {/* RECON Intel - AI Analysis - Compact */}
      {asset.type === 'token' && (
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
          <div className="flex items-center gap-2 mb-2">
            <Binoculars size={12} className="text-scout-gold" />
            <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-parchment uppercase tracking-wider">
              RECON Intel
            </span>
          </div>
          
          {insightsLoading ? (
            <div className="animate-pulse space-y-1.5">
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-full" />
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-3/4" />
            </div>
          ) : hasValidInsight ? (
            <div className="space-y-2">
              <p className="font-mono text-xs text-neutral-600 dark:text-parchment/85 leading-relaxed">
                {formatScannableText(tokenInsight.analysis)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <SignalBadge label="Momentum" value={tokenInsight.signals.momentum} />
                <SignalBadge label="Volume" value={tokenInsight.signals.volume} />
                <SignalBadge label="Volatility" value={tokenInsight.signals.volatility} />
              </div>
              {/* Additional context badges - BTC correlation (per-token, varies) */}
              {tokenInsight.globalContext?.btcCorrelation && (
                <div className="flex flex-wrap gap-1.5">
                  <ContextBadge 
                    label="vs BTC" 
                    value={tokenInsight.globalContext.btcCorrelation} 
                  />
                </div>
              )}
              <span className="font-mono text-[10px] text-neutral-500 dark:text-neutral-600">
                Updated {getTimeAgo(tokenInsight.timestamp)}
              </span>
            </div>
          ) : (
            <p className="font-mono text-xs text-neutral-500 italic">
              Analysis not available for this token yet.
            </p>
          )}
        </div>
      )}

      {/* NFT Collection Analysis - Compact */}
      {asset.type === 'nft' && (
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Binoculars size={12} className="text-scout-gold" />
              <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-parchment uppercase tracking-wider">
                RECON Intel
              </span>
              {/* OpenSea Verified Badge */}
              {asset.isVerified && (
                <Tooltip content="Verified on OpenSea - meets their verification criteria" position="top">
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-800/50 cursor-help">
                    <BadgeCheck size={10} />
                    <span className="text-[9px] font-mono font-medium">OpenSea</span>
                  </span>
                </Tooltip>
              )}
            </div>
            {/* Chain Badge - positioned top-right */}
            {hasValidCollectionInsight && collectionInsight.blockchain && (
              <ChainBadge chainId={collectionInsight.blockchain} />
            )}
          </div>
          
          {collectionLoading ? (
            <div className="animate-pulse space-y-1.5">
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-full" />
              <div className="h-3 bg-neutral-200 dark:bg-surface2 rounded w-3/4" />
            </div>
          ) : hasValidCollectionInsight ? (
            <div className="space-y-2">
              <p className="font-mono text-xs text-neutral-600 dark:text-parchment/85 leading-relaxed">
                {formatScannableText(collectionInsight.analysis)}
              </p>
              <span className="font-mono text-[10px] text-neutral-500 dark:text-neutral-600">
                Updated {getCollectionTimeAgo(collectionInsight.timestamp)}
              </span>
            </div>
          ) : (
            <p className="font-mono text-xs text-neutral-500 italic">
              Analysis not available for this collection yet.
            </p>
          )}
        </div>
      )}

      {/* Stats Grid - Token View (Compact) */}
      {asset.type === 'token' && (
        <div className="flex items-center border-b border-neutral-200 dark:border-divider divide-x divide-neutral-200 dark:divide-divider">
          <div className="flex-1 px-4 py-2.5">
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
              24H VOLUME
            </span>
            <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
              {asset.volumeFormatted}
            </span>
          </div>
          <div className="flex-1 px-4 py-2.5">
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
              MARKET CAP
            </span>
            <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
              {asset.marketCapFormatted || '—'}
            </span>
          </div>
        </div>
      )}

      {/* Stats Grid - NFT View (Expanded with OpenSea Data) */}
      {asset.type === 'nft' && (
        <div className="border-b border-neutral-200 dark:border-divider">
          {/* Row 1: Core Stats */}
          <div className="grid grid-cols-3 divide-x divide-neutral-200 dark:divide-divider">
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
                24H VOL
              </span>
              <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                {asset.volumeFormatted}
              </span>
            </div>
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider flex items-center gap-1">
                <Users size={10} /> OWNERS
              </span>
              <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                {asset.numOwners?.toLocaleString() || '—'}
              </span>
            </div>
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider flex items-center gap-1">
                <Layers size={10} /> SUPPLY
              </span>
              <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                {asset.totalSupply?.toLocaleString() || '—'}
              </span>
            </div>
          </div>
          
          {/* Row 2: Volume Trends */}
          <div className="grid grid-cols-3 divide-x divide-neutral-200 dark:divide-divider border-t border-neutral-200 dark:border-divider">
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
                AVG PRICE
              </span>
              <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                {asset.averagePrice ? `${asset.averagePrice.toFixed(4)} ${asset.currency}` : '—'}
              </span>
            </div>
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
                7D VOL
              </span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                  {asset.sevenDayVolume ? `${asset.sevenDayVolume.toFixed(1)}` : '—'}
                </span>
                {asset.sevenDayChange !== undefined && asset.sevenDayChange !== 0 && (
                  <span className={`font-mono text-[10px] ${asset.sevenDayChange >= 0 ? 'text-emerald-600 dark:text-signal-green-text' : 'text-rose-600 dark:text-alert-red-text'}`}>
                    {asset.sevenDayChange >= 0 ? '↑' : '↓'}{Math.abs(asset.sevenDayChange).toFixed(0)}%
                  </span>
                )}
              </div>
            </div>
            <div className="px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider block">
                30D VOL
              </span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-sm font-bold text-neutral-900 dark:text-parchment">
                  {asset.thirtyDayVolume ? `${asset.thirtyDayVolume.toFixed(1)}` : '—'}
                </span>
                {asset.thirtyDayChange !== undefined && asset.thirtyDayChange !== 0 && (
                  <span className={`font-mono text-[10px] ${asset.thirtyDayChange >= 0 ? 'text-emerald-600 dark:text-signal-green-text' : 'text-rose-600 dark:text-alert-red-text'}`}>
                    {asset.thirtyDayChange >= 0 ? '↑' : '↓'}{Math.abs(asset.thirtyDayChange).toFixed(0)}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Unique Owners % (if we have both owners and supply) */}
          {asset.numOwners && asset.totalSupply && asset.totalSupply > 0 && (
            <div className="px-3 py-2 border-t border-neutral-200 dark:border-divider bg-neutral-50 dark:bg-ink/60">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">
                  UNIQUE OWNERS
                </span>
                <span className="font-mono text-xs font-bold text-neutral-900 dark:text-parchment">
                  {((asset.numOwners / asset.totalSupply) * 100).toFixed(1)}%
                  <span className="text-neutral-500 font-normal ml-1">
                    ({asset.numOwners.toLocaleString()} / {asset.totalSupply.toLocaleString()})
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Social Links - NFT Only (from OpenSea) */}
      {asset.type === 'nft' && (asset.externalUrl || asset.twitterUsername || asset.discordUrl) && (
        <div className="px-4 py-2.5 border-b border-neutral-200 dark:border-divider flex items-center gap-2 flex-wrap">
          {asset.externalUrl && (
            <a
              href={asset.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-parchment bg-neutral-100 dark:bg-surface2 hover:bg-neutral-200 dark:hover:bg-surface2 rounded-md transition-colors"
            >
              <Globe size={12} />
              Website
            </a>
          )}
          {asset.twitterUsername && (
            <a
              href={`https://twitter.com/${asset.twitterUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-parchment bg-neutral-100 dark:bg-surface2 hover:bg-neutral-200 dark:hover:bg-surface2 rounded-md transition-colors"
            >
              <Twitter size={12} />
              @{asset.twitterUsername}
            </a>
          )}
          {asset.discordUrl && (
            <a
              href={asset.discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-parchment bg-neutral-100 dark:bg-surface2 hover:bg-neutral-200 dark:hover:bg-surface2 rounded-md transition-colors"
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Discord
            </a>
          )}
        </div>
      )}

      {/* Contract Address - NFT Only */}
      {asset.type === 'nft' && asset.contracts && asset.contracts.length > 0 && (
        <div className="px-4 py-2 border-b border-neutral-200 dark:border-divider">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">
              CONTRACT
            </span>
            <button
              onClick={() => handleCopyAddress(asset.contracts![0].address)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-parchment bg-neutral-100 dark:bg-surface2 hover:bg-neutral-200 dark:hover:bg-surface2 rounded transition-colors"
            >
              {copiedAddress ? (
                <>
                  <Check size={12} className="text-emerald-500" />
                  <span className="text-emerald-600 dark:text-signal-green-text">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={12} />
                  <span>{asset.contracts[0].address.slice(0, 6)}...{asset.contracts[0].address.slice(-4)}</span>
                  <span className="text-neutral-400">({asset.contracts[0].chain})</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Community Sentiment - Voting */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-divider">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Market Sentiment
          </span>
        </div>
        <SentimentVoteButtons
          collectionSlug={asset.type === 'token' ? asset.symbol || '' : asset.slug || ''}
          layout="horizontal"
          showPercentages={true}
          showVoteCounts={true}
        />
      </div>

      {/* Created Date - NFT Only (from OpenSea) */}
      {asset.type === 'nft' && asset.createdDate && (
        <div className="px-4 py-2 border-b border-neutral-200 dark:border-divider bg-neutral-50 dark:bg-ink/60">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">
              CREATED
            </span>
            <span className="font-mono text-xs text-neutral-700 dark:text-parchment/85">
              {new Date(asset.createdDate).toLocaleDateString('en-US', { 
                month: 'short', 
                year: 'numeric' 
              })}
              <span className="text-neutral-400 ml-1">
                ({(() => {
                  const created = new Date(asset.createdDate);
                  const now = new Date();
                  const months = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24 * 30));
                  if (months < 1) return 'new';
                  if (months < 12) return `${months}mo`;
                  const years = Math.floor(months / 12);
                  return `${years}y+`;
                })()})
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Action Buttons - Compact */}
      <div className="p-3 space-y-2 bg-neutral-50 dark:bg-transparent">
        {/* Only show View Profile for assets with glossary pages */}
        {asset.hasGlossaryPage && (
          <Link
            href={profileUrl}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-500 hover:bg-emerald-400 text-neutral-900 font-mono font-bold text-xs rounded-lg transition-colors"
          >
            <BarChart3 size={14} />
            View Profile
          </Link>
        )}
        <a
          href={tradeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center justify-center gap-2 w-full py-2 font-mono text-xs font-bold rounded-lg transition-colors ${
            asset.hasGlossaryPage 
              ? 'bg-neutral-200 dark:bg-surface2 hover:bg-neutral-300 dark:hover:bg-surface2 text-neutral-900 dark:text-parchment border border-neutral-300 dark:border-divider'
              : 'bg-emerald-500 hover:bg-emerald-400 text-neutral-900'
          }`}
        >
          {asset.type === 'token' ? 'Trade on DEX' : 'View on OpenSea'}
          <ExternalLink size={12} />
        </a>
      </div>

      {/* Share Modal */}
      {showShareModal && asset && (() => {
        // Get token insight for tokens
        const tokenInsight = asset.type === 'token' 
          ? getTokenInsight(insights, asset.symbol || '')
          : null;
        
        // Get collection insight for NFTs
        const collectionInsight = asset.type === 'nft'
          ? getCollectionInsight(collectionInsights, asset.slug || '')
          : null;

        // Find the token data from cryptoData for tokens
        const tokenData = asset.type === 'token' && cryptoData?.abstract_tokens
          ? cryptoData.abstract_tokens.find(t => t.symbol.toLowerCase() === (asset.symbol || '').toLowerCase())
          : null;

        if (asset.type === 'token' && tokenData) {
          return (
            <TagAssetShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              assetData={{
                type: 'token',
                token: tokenData,
                tokenName: asset.name,
                tokenSymbol: asset.symbol || '',
                insight: tokenInsight,
                abstractFearGreed: tokenPulse?.abstractFearGreed,
              }}
            />
          );
        } else if (asset.type === 'nft' && collectionStats) {
          const isAbstractChain = getCollectionBlockchain(asset.slug || '') === 'abstract';
          return (
            <TagAssetShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              assetData={{
                type: 'nft',
                stats: collectionStats,
                collectionName: asset.name,
                collectionSlug: asset.slug || '',
                currency: asset.currency,
                insight: collectionInsight,
                imageUrl: asset.image,
                abstractFearGreed: isAbstractChain ? tokenPulse?.abstractFearGreed : undefined,
                isAbstractChain,
              }}
            />
          );
        }
        
        // Fallback: close modal if data not available
        setShowShareModal(false);
        return null;
      })()}
    </div>
  );
}

/**
 * Signal Badge Component
 */
function SignalBadge({ label, value }: { label: string; value: string }) {
  const getColorClasses = () => {
    const normalizedValue = value?.toLowerCase().trim();
    
    if (label === 'Momentum') {
      if (normalizedValue === 'bullish') return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-signal-green-text border-emerald-300 dark:border-emerald-800/50';
      if (normalizedValue === 'bearish') return 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-alert-red-text border-rose-300 dark:border-rose-800/50';
    }
    if (label === 'Volume') {
      if (normalizedValue === 'above_average') return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-800/50';
      if (normalizedValue === 'below_average') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800/50';
    }
    if (label === 'Volatility') {
      if (normalizedValue === 'high') return 'bg-amber-100 dark:bg-scout-gold-dim text-amber-700 dark:text-scout-gold border-amber-300 dark:border-scout-gold/30';
      if (normalizedValue === 'low') return 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400 border-teal-300 dark:border-teal-800/50';
    }
    return 'bg-neutral-100 dark:bg-surface2 text-neutral-600 dark:text-neutral-400 border-neutral-300 dark:border-divider';
  };

  const formatValue = () => {
    return value.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono font-medium ${getColorClasses()}`}>
      <span className="font-semibold">{label}:</span>
      <span>{formatValue()}</span>
    </span>
  );
}

/**
 * Context Badge Component - for BTC correlation and other context
 * Includes tooltips to explain what each badge means
 */
function ContextBadge({ label, value }: { label: string; value: string }) {
  const getColorClasses = () => {
    const normalizedValue = value?.toLowerCase().trim();
    
    if (label === 'vs BTC') {
      if (normalizedValue === 'correlated') return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-800/50';
      if (normalizedValue === 'diverging') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800/50';
      if (normalizedValue === 'independent') return 'bg-slate-100 dark:bg-slate-900/40 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-800/50';
    }
    return 'bg-neutral-100 dark:bg-surface2 text-neutral-600 dark:text-neutral-400 border-neutral-300 dark:border-divider';
  };

  const formatValue = () => {
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  // Get tooltip content based on label and value
  const getTooltipContent = () => {
    if (label === 'vs BTC') {
      const v = value?.toLowerCase().trim();
      if (v === 'correlated') return 'Token price moves in sync with Bitcoin';
      if (v === 'diverging') return 'Token price moving opposite to Bitcoin';
      if (v === 'independent') return 'Token price moving independently of Bitcoin';
    }
    return '';
  };

  const tooltipContent = getTooltipContent();

  return (
    <Tooltip content={tooltipContent} position="top">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono font-medium cursor-help ${getColorClasses()}`}>
        <span className="font-semibold">{label}:</span>
        <span>{formatValue()}</span>
      </span>
    </Tooltip>
  );
}

